type HttpMethod = "GET" | "POST" | "PUT" | "DELETE"

export class HTTPError extends Error {
    name: string
    code: number
    message: string
    payload: { [key: string]: any } | null

    constructor(response: Response, jsonPayload?: { [key: string]: any }) {
        super(response.statusText)
        this.name = "HTTPError"
        this.code = response.status
        this.message = jsonPayload?.message || response.statusText
        this.payload = jsonPayload || null
    }
}

export class AccessTokenExpiredError extends HTTPError {
    constructor(response: Response, jsonPayload?: { [key: string]: any }) {
        super(response, jsonPayload)
        this.name = "AccessTokenExpiredError"
    }
}

const CSRF_TOKEN_HEADER = "X-CSRF-TOKEN"
const CSRF_ACCESS_TOKEN_NAME = "csrf_access_token"
const CSRF_REFRESH_TOKEN_NAME = "csrf_refresh_token"

const REFRESH_ENDPOINT = "/token/refresh"

const _createHTTPError = (
    response: Response,
    jsonPayload?: { [key: string]: any }
) => {
    const ACCESS_TOKEN_EXPIRED_MESSAGE = "Access token expired."

    if (
        response.status === 401 &&
        jsonPayload?.message === ACCESS_TOKEN_EXPIRED_MESSAGE
    )
        return new AccessTokenExpiredError(response, jsonPayload)

    return new HTTPError(response, jsonPayload)
}

// Client for internal API calls. Base URL is defined in .env files.
const apiClient = async (
    method: HttpMethod,
    endpoint: string,
    {
        payload = null,
        params = null,
        headers = null,
        csrfTokenType = "access",
        _isRetry = false,
    }: {
        payload?: any
        params?: { [key: string]: string | number | boolean | string[] } | null
        headers?: { [key: string]: string } | null
        csrfTokenType?: "access" | "refresh"
        _isRetry?: boolean
    } = {
        payload: null,
        params: null,
        headers: null,
        csrfTokenType: "access",
        _isRetry: false,
    }
): Promise<any> => {
    const baseURL = import.meta.env.VITE_API_BASEURL || window.location.origin

    if (!endpoint.startsWith("/")) endpoint = "/" + endpoint

    const url = new URL(import.meta.env.VITE_API_PREFIX + endpoint, baseURL)

    // Headers config
    if (headers === null) headers = {}

    // Add CSRF token to state-changing request (if available)
    const csrfToken = getCsrfFromCookie(
        csrfTokenType === "access"
            ? CSRF_ACCESS_TOKEN_NAME
            : CSRF_REFRESH_TOKEN_NAME
    )
    if (csrfToken && method !== "GET") headers[CSRF_TOKEN_HEADER] = csrfToken

    let req: RequestInit = {
        method,
        headers: Object.assign({}, apiClient.headers, headers ?? {}),
        mode: "cors",
        credentials: "include",
    }

    if (params != null)
        Object.entries(params).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach((item) => url.searchParams.append(k, item))
            } else if (v) {
                url.searchParams.append(k, String(v))
            }
        })

    if (payload != null) req["body"] = JSON.stringify(payload)

    return fetch(url, req).then(async (response) => {
        if (!response.ok) {
            let jsonPayload: { [k: string]: any }
            try {
                jsonPayload = await response.json()
            } catch {
                jsonPayload = {}
            }

            const error = _createHTTPError(response, jsonPayload)

            // Handle token refresh
            if (
                error instanceof AccessTokenExpiredError &&
                !_isRetry &&
                endpoint !== REFRESH_ENDPOINT
            )
                try {
                    await apiClient.refreshToken()

                    return apiClient(method, endpoint, {
                        payload,
                        params,
                        headers,
                        _isRetry: true,
                    })
                } catch {
                    // Fall through to raise the original error.
                }

            throw error
        } else if (
            response.headers.get("Content-Type")?.includes("application/json")
        ) {
            return response.json()
        } else {
            return response.text()
        }
    })
}

/** Retrieve the CSRF Token from the cookie.
 *
 * The name should match the `JWT_ACCESS_CSRF_COOKIE_NAME` const in the relevant Flask
 * config file (e.g. instance/default.py, instance/prod.py, ...).
 *
 * See https://flask-jwt-extended.readthedocs.io/en/stable/token_locations.html#cookies
 * for details.
 *
 */
export const getCsrfFromCookie = (name: string = CSRF_ACCESS_TOKEN_NAME) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts && parts.length === 2) return parts.pop()?.split(";").shift()
    return null
}

apiClient.headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
} as { [key: string]: string }

apiClient.get = apiClient.bind(null, "GET")
apiClient.post = apiClient.bind(null, "POST")
apiClient.put = apiClient.bind(null, "PUT")
apiClient.delete = apiClient.bind(null, "DELETE")
apiClient.refreshToken = () =>
    apiClient.post(REFRESH_ENDPOINT, {
        csrfTokenType: "refresh",
    })

/** Helper function to globablly set the organization header in all requests */
apiClient.setOrganizationHeader = (organizationId: string | null) => {
    if (organizationId) apiClient.headers["X-Organization"] = organizationId
    else delete apiClient.headers["X-Organization"]
}

/** Download a blob from the API and trigger browser download */
apiClient.downloadBlob = async (
    endpoint: string,
    {
        params = null,
        defaultFilename = "download",
    }: {
        params?: { [key: string]: string | number | boolean | string[] } | null
        defaultFilename?: string
    } = {}
) => {
    const baseURL = import.meta.env.VITE_API_BASEURL || window.location.origin

    if (!endpoint.startsWith("/")) endpoint = "/" + endpoint

    const url = new URL(import.meta.env.VITE_API_PREFIX + endpoint, baseURL)

    if (params != null)
        Object.entries(params).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach((item) => url.searchParams.append(k, item))
            } else if (v) {
                url.searchParams.append(k, String(v))
            }
        })

    const response = await fetch(url, { credentials: "include" })

    if (!response.ok) {
        let errorPayload
        try {
            errorPayload = await response.json()
        } catch {
            errorPayload = {}
        }
        throw new HTTPError(response, errorPayload)
    }

    const filename =
        response.headers.get("X-Suggested-Filename") || defaultFilename
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = downloadUrl
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
}

export { apiClient }
